import { useCallback, useContext, useLayoutEffect, useState } from "react";
import { PLACEMENT_KEY_ATTRIBUTE } from "./boardAttributes";
import { getVisibleArea, type VisibleArea } from "./boardLanding";
import { BoardScrollerContext } from "./useBoardWindow";

/** The edge of the column's view a slot is scrolled past. */
export type BoardSlotEdge = "above" | "below";

/** Where the dragged card's slot sits in its cell. */
export interface BoardSlotSpot {
    /** The slot's middle, from the cell's top. */
    middle: number;
    /** Set while the slot's middle is out of the column's view. */
    edge: BoardSlotEdge | null;
}

const getEdge = (middle: number, view: VisibleArea) => {
    if (middle < view.top) {
        return "above";
    }
    return middle > view.bottom ? "below" : null;
};

const measureSlotSpot = ({
    cell,
    slot,
    scroller,
}: {
    cell: Element;
    slot: Element;
    scroller: HTMLElement;
}): BoardSlotSpot => {
    const { top, height } = slot.getBoundingClientRect();
    const middle = top + height / 2;
    return {
        middle: Math.round(middle - cell.getBoundingClientRect().top),
        edge: getEdge(middle, getVisibleArea(scroller)),
    };
};

const isSameSpot = (
    first: BoardSlotSpot | null,
    second: BoardSlotSpot | null,
) => first?.middle === second?.middle && first?.edge === second?.edge;

/**
 * Where the slot of `slotKey` sits in the cell `layer` is laid over. Read
 * after every render, since the slot moves with the cell's cards, as the
 * column scrolls, and as the cell resizes (windowed cards settling their
 * heights). Null until measured, and while the slot isn't mounted.
 */
export const useBoardSlotSpot = (
    layer: HTMLElement | null,
    slotKey: string,
) => {
    const { element: scroller } = useContext(BoardScrollerContext);
    const [spot, setSpot] = useState<BoardSlotSpot | null>(null);
    const measure = useCallback(() => {
        const cell = layer?.parentElement;
        if (cell === undefined || cell === null || scroller === null) {
            return;
        }
        const slot = cell.querySelector(
            `[${PLACEMENT_KEY_ATTRIBUTE}="${CSS.escape(slotKey)}"]`,
        );
        const next =
            slot === null ? null : measureSlotSpot({ cell, slot, scroller });
        setSpot((previous) => (isSameSpot(previous, next) ? previous : next));
    }, [layer, scroller, slotKey]);
    useLayoutEffect(() => {
        measure();
    });
    useLayoutEffect(() => {
        const cell = layer?.parentElement;
        if (scroller === null || cell === undefined || cell === null) {
            return undefined;
        }
        scroller.addEventListener("scroll", measure, { passive: true });
        const cellObserver = new ResizeObserver(measure);
        cellObserver.observe(cell);
        return () => {
            scroller.removeEventListener("scroll", measure);
            cellObserver.disconnect();
        };
    }, [layer, scroller, measure]);
    return spot;
};
